import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import dayjs from 'dayjs'
import { conversationApi } from '@/shared/api/services'
import type { Conversation as ApiConversation, Message as ApiMessage } from '@/shared/api/types'

export interface Conversation {
  id: string
  customer: string
  customerId: string
  startTime: string
  endTime: string
  duration: string
  status: 'completed' | 'ongoing' | 'cancelled'
  satisfaction?: number
  tags: string[]
}

export interface ConversationDetail {
  id: string
  messages: Message[]
  customerInfo: CustomerInfo
  summary: string
}

export interface Message {
  id: string
  content: string
  sender: 'customer' | 'agent'
  timestamp: string
}

export interface CustomerInfo {
  name: string
  email?: string
  phone?: string
  location?: string
  segment?: string
}

export interface HistoryFilter {
  searchText: string
  status: string
  sortBy: string
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs]
}

export interface HistoryState {
  conversations: Conversation[]
  filteredConversations: Conversation[]
  selectedConversation: ConversationDetail | null
  filter: HistoryFilter
  isLoading: boolean
  error: string | null
  isDetailModalOpen: boolean

  // Actions
  setFilter: (filter: Partial<HistoryFilter>) => void
  setSelectedConversation: (conversation: ConversationDetail | null) => void
  setIsDetailModalOpen: (isOpen: boolean) => void
  fetchConversations: () => Promise<void>
  fetchConversationDetail: (id: string) => Promise<void>
  exportConversations: () => Promise<void>
  applyFilters: () => void
  resetFilters: () => void
}

const initialState = {
  conversations: [],
  filteredConversations: [],
  selectedConversation: null,
  filter: {
    searchText: '',
    status: 'all',
    sortBy: 'time',
  },
  isLoading: false,
  error: null,
  isDetailModalOpen: false,
}

export const useHistoryStore = create<HistoryState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setFilter: (filter) => {
        set((state) => ({
          filter: { ...state.filter, ...filter },
        }))
        get().applyFilters()
      },

      setSelectedConversation: (conversation) => {
        set({ selectedConversation: conversation })
      },

      setIsDetailModalOpen: (isOpen) => {
        set({ isDetailModalOpen: isOpen })
      },

      fetchConversations: async () => {
        set({ isLoading: true, error: null })
        try {
          // 调用真实API获取对话列表
          const response = await conversationApi.getConversations()
          const backendConversations = response?.conversations || []

          // 转换后端数据格式以匹配前端Conversation接口
          const conversations: Conversation[] = backendConversations.map((conv: ApiConversation) => {
            // 计算持续时间（加 Z 确保 dayjs 按 UTC 解析）
            const toUtc = (s: string) => s ? (s.endsWith('Z') ? s : s + 'Z') : s
            const startTime = toUtc(conv.created_at)
            const endTime = toUtc(conv.last_activity || conv.created_at)
            const start = dayjs(startTime)
            const end = dayjs(endTime)
            const durationMs = end.diff(start)
            const durationMinutes = Math.floor(durationMs / 60000)
            const durationSeconds = Math.floor((durationMs % 60000) / 1000)
            const duration = durationMinutes > 0 ? `${durationMinutes}分${durationSeconds}秒` : `${durationSeconds}秒`

            // 状态直接映射后端 status 字段
            let status: 'completed' | 'ongoing' | 'cancelled' = 'completed'
            if (conv.status === 'active' || conv.status === 'pending_human' || conv.status === 'human_active') {
              status = 'ongoing'
            } else if (conv.status === 'deleted') {
              status = 'cancelled'
            }

            // 客户信息：使用platform_id作为客户ID，名字可以简化为"客户" + 部分ID
            const customerId = conv.customer_platform_id || conv.conversation_id
            const customerName = `客户${customerId.slice(-4)}`

            // 标签：根据平台和消息数量生成
            const tags: string[] = []
            if (conv.platform) tags.push(conv.platform)
            if (conv.message_count > 5) tags.push('多轮对话')
            if (conv.conversation_summary) tags.push('有摘要')

            return {
              id: conv.conversation_id,
              customer: customerName,
              customerId: customerId,
              startTime: start.format('YYYY-MM-DD HH:mm:ss'),
              endTime: end.format('YYYY-MM-DD HH:mm:ss'),
              duration,
              status,
              satisfaction: conv.satisfaction ?? undefined,
              tags
            }
          })

          set({ conversations, filteredConversations: conversations, isLoading: false })
        } catch (error) {
          console.error('获取历史记录失败:', error)
          set({ error: '获取历史记录失败，请检查后端服务', isLoading: false })
        }
      },

      fetchConversationDetail: async (id: string) => {
        set({ isLoading: true, error: null })
        try {
          // 调用真实API获取对话历史
          const response = await conversationApi.getConversationHistory(id)
          const backendData = response || {}

          // 转换消息格式
          const messages: Message[] = (backendData.messages || []).map((msg: ApiMessage, index: number) => {
            let timestamp = '未知时间'
            try {
              if (msg.timestamp) {
                const ts = msg.timestamp.endsWith('Z') ? msg.timestamp : msg.timestamp + 'Z'
                timestamp = dayjs(ts).format('HH:mm:ss')
              }
            } catch (e) {
              console.warn('时间戳格式化失败:', msg.timestamp, e)
            }
            return {
              id: `msg_${index + 1}`,
              content: msg.content || '',
              sender: msg.role === 'user' ? 'customer' : 'agent',
              timestamp
            }
          })

          // 尝试从对话列表中获取更多信息
          const conversations = get().conversations
          const conversationInfo = conversations.find(conv => conv.id === id)

          // 构建客户信息
          const customerInfo: CustomerInfo = {
            name: conversationInfo?.customer || `客户${id.slice(-4)}`,
          }

          const detail: ConversationDetail = {
            id,
            messages,
            customerInfo,
            summary: backendData.conversation_summary || `对话包含${messages.length}条消息`
          }

          set({ selectedConversation: detail, isLoading: false, isDetailModalOpen: true })
        } catch (error) {
          console.error('获取对话详情失败:', error)
          set({ error: '获取对话详情失败，请检查后端服务', isLoading: false })
        }
      },

      exportConversations: async () => {
        set({ isLoading: true, error: null })
        try {
          // 模拟API调用
          // const response = await api.history.exportConversations(get().filter)

          // 模拟导出
          setTimeout(() => {
            set({ isLoading: false })
          }, 1000)
        } catch (error) {
          set({ error: '导出数据失败', isLoading: false })
        }
      },

      applyFilters: () => {
        const { conversations, filter } = get()

        let filtered = [...conversations]

        // 搜索过滤
        if (filter.searchText) {
          const searchLower = filter.searchText.toLowerCase()
          filtered = filtered.filter(
            (conv) =>
              conv.customer.toLowerCase().includes(searchLower) ||
              conv.customerId.toLowerCase().includes(searchLower) ||
              conv.tags.some((tag) => tag.toLowerCase().includes(searchLower))
          )
        }

        // 状态过滤
        if (filter.status !== 'all') {
          filtered = filtered.filter((conv) => conv.status === filter.status)
        }

        // 日期范围过滤
        if (filter.dateRange) {
          const [start, end] = filter.dateRange
          filtered = filtered.filter((conv) => {
            const convDate = dayjs(conv.startTime)
            return convDate.isAfter(start.startOf('day')) && convDate.isBefore(end.endOf('day'))
          })
        }

        // 排序
        if (filter.sortBy === 'satisfaction') {
          filtered.sort((a, b) => {
            const sa = a.satisfaction ?? 0
            const sb = b.satisfaction ?? 0
            if (sb !== sa) return sb - sa
            return dayjs(b.startTime).unix() - dayjs(a.startTime).unix()
          })
        } else {
          filtered.sort((a, b) => dayjs(b.startTime).unix() - dayjs(a.startTime).unix())
        }

        set({ filteredConversations: filtered })
      },

      resetFilters: () => {
        set({
          filter: {
            searchText: '',
            status: 'all',
            sortBy: 'time',
            dateRange: undefined,
          },
        })
        set((state) => ({ filteredConversations: state.conversations }))
      },
    }),
    { name: 'HistoryStore' }
  )
)